import { BigDataEventConstants } from "./constants/big-data-constants";
import { EventType } from "./constants/event-type-constants";
import { MarketingClientConstants } from "./constants/marketing-client-constants";
import { NewsSwimmerConstants } from "./constants/news-swimmer-constants";
import { NewsletterSaverConstants } from "./constants/newsletter-saver-constants";
import { RwErrorMessage } from "./constants/rw-object.constants";
import { AllowedURLParamTypes } from "./custom-types/custom-types";
import { Attribute, BigDataHelper } from "./helpers/big-data-helper";
import { DeviceInformationHelper, DeviceType } from "./helpers/device-information-helper";
import { DOMHelper } from "./helpers/dom-helper";
import { httpInstance } from "./http/http";
import { WYBORCZA_PL } from "./marketing-client";
import { BigData } from "./marketing-client-bigdata";
import { Capping } from "./marketing-client-capping";
import { Utilization } from "./marketing-client-utilization";
import { ErrorHandler } from "./utils/error-handler-first-rw";
import { Utils } from "./utils/utils";

export class NewsletterSaver {
    agreementsExtendButtons: Element[];
    agreementsExtended = false;
    emailInput: HTMLInputElement;
    emailErrorHandler: ErrorHandler;
    isMobile: boolean;
    messages = RwErrorMessage;
    newsletterSaver: HTMLElement;
    parent: HTMLElement | null;
    subscribeToNewsletterButton: HTMLElement;
    newsletterId: string;
    isHarmonyTest: AllowedURLParamTypes = Utils.getURLParamsAsObject().enableHarmonyTest;
    path: string = this.isHarmonyTest
        ? "https://coreos07.wyborcza.pl:8094/squid-news/profiles"
        : "https://squid-api.wyborcza.pl/squid-news/profiles";

    constructor(public capping: Capping, public utilizationObject: Utilization) {
        this.isMobile = DeviceInformationHelper.getDeviceType() === DeviceType.MOBILE;
    }

    init() {
        this.initializeNewsletterSaver();
        this.initializeNewsletterId();
        this.initializeEmailInput();
        this.initializeButtons();
        this.initializeParent();
        this.bindEvents();
    }

    initializeNewsletterSaver(): void {
        this.newsletterSaver = DOMHelper.querySelector(NewsletterSaverConstants.newsSaverClass) as HTMLElement;
    }

    initializeNewsletterId(): void {
        if (this.newsletterSaver?.dataset?.newsletterId) this.newsletterId = this.newsletterSaver.dataset.newsletterId;
    }

    initializeEmailInput() {
        this.emailInput = DOMHelper.querySelector(NewsletterSaverConstants.emailClass, this.newsletterSaver) as HTMLInputElement;
        this.emailErrorHandler = new ErrorHandler(this.emailInput);
        if (WYBORCZA_PL?.userInfo?.loggedIn && this.emailInput) this.emailInput.value = WYBORCZA_PL.userInfo.email;
    }

    initializeButtons() {
        this.agreementsExtendButtons = DOMHelper.querySelectorAsArray(NewsletterSaverConstants.moreButtonClass, this.newsletterSaver);
        this.subscribeToNewsletterButton = DOMHelper.querySelector(
            NewsletterSaverConstants.buttonWrapperClass,
            this.newsletterSaver
        ) as HTMLElement;
    }

    initializeParent(): void {
        this.parent =
            this.newsletterSaver && this.newsletterSaver.closest(`[${MarketingClientConstants.placeholderDataSetAttribute}]`);
    }

    bindEvents(): void {
        this.bindEventsToNewsletterSubscription();
        this.bindEventsToEmailInput();
        this.bindEventsToAgreementsExtendButton();
    }

    bindEventsToNewsletterSubscription(): void {
        if (this.subscribeToNewsletterButton) {
            this.subscribeToNewsletterButton.addEventListener(EventType.CLICK, (event) => {
                this.subscribeToNewsletterButton.setAttribute(MarketingClientConstants.disabledClassName, "");
                event.preventDefault();
                this.emailErrorHandler.hide();
                if (this.validateEmail()) {
                    this.utilize();
                    this.subscribe();
                } else {
                    this.subscribeToNewsletterButton.removeAttribute(MarketingClientConstants.disabledClassName);
                }
            });
        }
    }

    bindEventsToEmailInput(): void {
        if (this.emailInput) {
            this.emailInput.addEventListener(EventType.CHANGE, this.validateEmail.bind(this));
            this.emailInput.addEventListener(EventType.INPUT, () => {
                return this.emailInput?.value.length
                    ? this.emailErrorHandler.hide()
                    : this.emailErrorHandler.show(this.messages.required);
            });
        }
    }

    bindEventsToAgreementsExtendButton() {
        this.agreementsExtendButtons.forEach(button => {
            button.addEventListener(EventType.CLICK, (event) => {
                this.agreementsExtended = !this.agreementsExtended;
                this.toggleAgreements(button);
            });
        });
    }

    toggleAgreements(button: Element) {
        button.textContent = this.agreementsExtended ? NewsletterSaverConstants.less : NewsletterSaverConstants.more;

        const agreementTextFields = DOMHelper.querySelectorAsArray(NewsletterSaverConstants.agreementTextClass, this.newsletterSaver);
        agreementTextFields.forEach((agreementTextField: Element) => {
            this.agreementsExtended ? agreementTextField.classList.add(NewsletterSaverConstants.lineClamps) : agreementTextField.classList.remove(NewsletterSaverConstants.lineClamps);
        });
    }

    validateEmail(): boolean {
        const value = this.emailInput ? this.emailInput.value : "",
            emailIsValid = Utils.validateEmail(value);

        if (!value.length) {
            return this.emailErrorHandler.show(this.messages.required);
        } else if (!emailIsValid) {
            return this.emailErrorHandler.show(this.messages.banned);
        } else {
            return this.emailErrorHandler.hide();
        }
    }

    async utilize(clickID = "", onlyBigData = false) {
        const condition = "brak";

        if (this.parent) {
            const dataToSend = this.utilizationObject.prepareDataToSend(this.parent, condition),
                attribute: Attribute = BigDataHelper.createAttribute(this.parent, true, clickID);

            if (!onlyBigData) {
                this.utilizationObject.utilize(dataToSend);
            }

            BigData.sendDataOceanEvent(BigDataEventConstants.onClick, { ...dataToSend, ...attribute });
            BigData.sendDataOceanEvent(BigDataEventConstants.onAddingToNewsletter, {
                hm: await this.sha256(this.emailInput.value),
                newsletter: [this.newsletterId]
            });
        }
    }

    async sha256(message: string) {
        // encode as UTF-8
        const msgBuffer = new TextEncoder().encode(message);

        // hash the message
        const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);

        // convert ArrayBuffer to Array
        const hashArray = Array.from(new Uint8Array(hashBuffer));

        // convert bytes to hex string
        const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
        return hashHex;
    }

    async subscribe() {
        const data = {
            email: this.emailInput.value,
            newsletters: [this.newsletterId],
            confirmed: false
        };

        await httpInstance
            .post({
                url: this.path,
                data: data,
                withCredentials: true,
                headers: {
                    "Content-Type": "application/json",
                    Accept: "*/*"
                }
            })
            .then(this.successNewsletterSubscription.bind(this), this.errorNewsletterSubscription.bind(this));
    }

    successNewsletterSubscription() {
        const step1 = DOMHelper.querySelector(NewsletterSaverConstants.step1Class, this.newsletterSaver) as HTMLElement,
            step2 = DOMHelper.querySelector(NewsletterSaverConstants.step2Class, this.newsletterSaver) as HTMLElement;

        this.hideNewsSwimmerButtonCloseStep2();

        step1.classList.toggle(MarketingClientConstants.hiddenClassName);
        step2.classList.toggle(MarketingClientConstants.hiddenClassName);
        this.appendUserEmail(step2);
    }

    hideNewsSwimmerButtonCloseStep2() {
        const newsSwimmerButtonCloseStep2 = DOMHelper.querySelector(NewsSwimmerConstants.newsSwimmerWrapperClass);
        newsSwimmerButtonCloseStep2?.classList.add(MarketingClientConstants.hiddenClassName);
    }

    appendUserEmail(step2: HTMLElement) {
        const mailBox = step2.querySelector(NewsletterSaverConstants.step2EmailClass);
        if (mailBox) mailBox.textContent = this.emailInput.value;
    }

    errorNewsletterSubscription() {
        this.emailErrorHandler.show(this.messages.subscriptionError);
        this.subscribeToNewsletterButton.removeAttribute(MarketingClientConstants.disabledClassName);
    }
}
